import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChefHat, Clock, Users, Plus, Mail, Sparkles, Loader2, ShoppingCart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecipeOfTheWeek({ variant = "homepage" }) {
  const { user } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [logging, setLogging] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/recipes/current`);
        if (!cancelled) setRecipe(data?.recipe || null);
      } catch (e) {
        // ignore — homepage shouldn't blow up if recipes are temporarily unavailable
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const emailMe = async () => {
    if (!user) {
      toast.info("Sign in to email yourself this recipe.");
      return;
    }
    if (!recipe?.recipe_id) return;
    setEmailing(true);
    try {
      await axios.post(`${API}/recipes/${recipe.recipe_id}/email-me`);
      toast.success(`📧 Recipe sent to ${user.email}!`, { duration: 4500 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed.");
    } finally {
      setEmailing(false);
    }
  };

  const addToLog = async () => {
    if (!user) {
      toast.info("Sign in to add this to your food log.");
      return;
    }
    if (!recipe?.recipe_id) return;
    setLogging(true);
    try {
      await axios.post(`${API}/recipes/${recipe.recipe_id}/add-to-log`);
      toast.success(`🍳 Logged ${recipe.macros_per_serving?.calories || 0} cal · +5 Phoenix Tokens!`, { duration: 4000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logging failed.");
    } finally {
      setLogging(false);
    }
  };

  const addIngredientsToShopping = async () => {
    if (!user) {
      toast.error("Sign in to use the Shopping List.");
      return;
    }
    if (!recipe?.recipe_id) return;
    try {
      const { data } = await axios.post(`${API}/shopping/import-recipe/${recipe.recipe_id}`);
      toast.success(`🛒 Added ${data?.added || 0} ingredients to your Shopping List.`);
      window.dispatchEvent(new CustomEvent("omnimuscle:open-shopping"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import ingredients.");
    }
  };

  if (loading) {
    return (
      <section
        data-testid="recipe-week-loading"
        className="py-20 bg-[#0A0A0A] flex items-center justify-center"
      >
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </section>
    );
  }
  if (!recipe) return null;

  const macros = recipe.macros_per_serving || {};
  const tags = recipe.tags || [];
  const ingredients = recipe.ingredients || [];
  const instructions = recipe.instructions || [];

  const wrapperClass =
    variant === "homepage"
      ? "py-20 md:py-28 bg-gradient-to-b from-[#0A0A0A] via-[#120824] to-[#0A0A0A]"
      : "py-8 bg-[#0F0F0F] border border-zinc-800 my-6";

  return (
    <section
      id="recipe-of-the-week"
      data-testid="recipe-of-the-week"
      className={wrapperClass}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] mb-4">
            <ChefHat size={14} />
            <span className="font-accent text-xs tracking-[0.3em] uppercase">
              Recipe of the Week
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-3">
            {recipe.title}
          </h2>
          {recipe.tagline && (
            <p className="text-[#D4AF37] italic max-w-2xl mx-auto text-sm md:text-base">
              "{recipe.tagline}"
            </p>
          )}
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-3 gap-3 mb-3 max-w-2xl mx-auto">
          <div className="bg-[#0F0F0F] border border-zinc-800 p-4 text-center">
            <Clock size={14} className="text-[#D4AF37] mx-auto mb-1" />
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Prep</div>
            <div className="font-display text-2xl text-white">{recipe.prep_min || 0}<span className="text-xs text-neutral-500">m</span></div>
          </div>
          <div className="bg-[#0F0F0F] border border-zinc-800 p-4 text-center">
            <ChefHat size={14} className="text-[#D4AF37] mx-auto mb-1" />
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cook</div>
            <div className="font-display text-2xl text-white">{recipe.cook_min || 0}<span className="text-xs text-neutral-500">m</span></div>
          </div>
          <div className="bg-[#0F0F0F] border border-zinc-800 p-4 text-center">
            <Users size={14} className="text-[#D4AF37] mx-auto mb-1" />
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Servings</div>
            <div className="font-display text-2xl text-white">{recipe.servings || 1}</div>
          </div>
        </div>

        {/* Macros */}
        <div className="grid grid-cols-4 gap-2 mb-5 max-w-2xl mx-auto">
          {[
            { k: "Cal", v: macros.calories },
            { k: "Protein", v: `${macros.protein_g || 0}g` },
            { k: "Carbs", v: `${macros.carbs_g || 0}g` },
            { k: "Fat", v: `${macros.fat_g || 0}g` },
          ].map((m) => (
            <div key={m.k} className="bg-[#1a0d2b] border border-[#4C1D95] p-3 text-center">
              <div className="text-[9px] uppercase tracking-widest text-[#D4AF37]">{m.k}</div>
              <div className="font-display text-xl text-white">{m.v}</div>
            </div>
          ))}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-6 max-w-3xl mx-auto">
            {tags.map((t) => (
              <span
                key={t}
                className="px-2.5 py-1 text-[10px] uppercase tracking-widest bg-[#0A0A0A] border border-[#D4AF37]/30 text-[#D4AF37]"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Action bar */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          <button
            data-testid="recipe-add-to-log"
            onClick={addToLog}
            disabled={logging || !user}
            className="btn-omni btn-red disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {logging ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add to Food Log
          </button>
          <button
            data-testid="recipe-add-to-shopping"
            onClick={addIngredientsToShopping}
            disabled={!user}
            className="btn-omni btn-outline-gold disabled:opacity-50 disabled:cursor-not-allowed"
            title="Premium: bulk-add every ingredient to your Shopping List"
          >
            <ShoppingCart size={14} />
            To Shopping List
          </button>
          <button
            data-testid="recipe-email-me"
            onClick={emailMe}
            disabled={emailing || !user}
            className="btn-omni btn-outline-gold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {emailing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            Email Me
          </button>
          <button
            data-testid="recipe-expand"
            onClick={() => setExpanded(!expanded)}
            className="btn-omni btn-outline-gold"
          >
            <Sparkles size={14} />
            {expanded ? "Hide Recipe" : "View Recipe"}
          </button>
        </div>

        {/* Full recipe — collapsible */}
        {expanded && (
          <div
            data-testid="recipe-full"
            className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto bg-[#0F0F0F] border border-zinc-800 p-6 md:p-8"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3">
                Ingredients
              </div>
              <ul className="space-y-2">
                {ingredients.map((it, i) => (
                  <li
                    key={i}
                    className="text-sm text-neutral-200 flex items-start gap-2"
                  >
                    <span className="text-[#D4AF37] flex-shrink-0 mt-1">▸</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-3">
                Instructions
              </div>
              <ol className="space-y-3">
                {instructions.map((step, i) => (
                  <li
                    key={i}
                    className="text-sm text-neutral-200 flex items-start gap-3"
                  >
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-[#D4AF37] text-black font-bold text-xs flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}

        <p className="text-center text-[10px] uppercase tracking-[0.3em] text-neutral-600 mt-6">
          {recipe.source === "ai" ? "AI Coach OMNI · " : "Curated by Roc · "}
          Rotates every Monday 00:00 PT
        </p>
      </div>
    </section>
  );
}
